import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { Truck, BadgeCheck, Plus, Trash2, Send, Inbox } from "lucide-react";
import { toast } from "sonner";
import OrgTeamPanel from "@/components/OrgTeamPanel";
import VendorTechnicalPanel from "@/components/vendor/VendorTechnicalPanel";
import VendorSuppliesPanel from "@/components/vendor/VendorSuppliesPanel";

/** Vendor dashboard
 *  Catalog editor (live) · Lead inbox (placeholder) · Push slot status. */
export default function VendorDashboard() {
  const [org, setOrg] = useState(null);
  const [vendorMeta, setVendorMeta] = useState({ sections: {} });
  const [catalog, setCatalog] = useState([]);
  const [addOpen, setAddOpen] = useState(false);

  const refresh = async () => {
    const [{ data: orgResp }, { data: meta }] = await Promise.all([
      api.get("/onboard/me/organization"),
      api.get("/meta/vendor-categories"),
    ]);
    setOrg(orgResp.organization);
    setVendorMeta(meta);
    if (orgResp.organization) {
      const { data: cat } = await api.get(`/orgs/${orgResp.organization.id}/catalog`);
      setCatalog(cat.items || []);
    }
  };
  useEffect(() => { void refresh(); }, []);

  const removeItem = async (itemId) => {
    if (!window.confirm("Remove this item from your catalog?")) return;
    try {
      await api.delete(`/orgs/${org.id}/catalog/${itemId}`);
      toast.success("Removed");
      await refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not remove");
    }
  };

  if (!org) return <div className="mx-auto max-w-7xl px-6 py-16 text-center text-white/55">Loading dashboard…</div>;
  if (org.type !== "vendor") {
    return (
      <div className="mx-auto max-w-3xl px-6 py-16">
        <div className="cs-card rounded-2xl p-10 text-center">
          <Truck size={32} className="mx-auto text-white/40 mb-4" />
          <h1 className="font-display text-2xl uppercase tracking-tight">Vendor Dashboard</h1>
          <p className="mt-3 text-sm text-white/65">Your org is registered as "{org.type}", not a vendor.</p>
        </div>
      </div>
    );
  }

  // Map ticked category keys → full meta
  const flatCats = Object.values(vendorMeta.sections || {}).flat();
  const pickedCats = (org.vendor_categories || [])
    .map((k) => flatCats.find((c) => c.key === k))
    .filter(Boolean);

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10" data-testid="vendor-dashboard">
      <div className="text-[10px] tracking-[0.3em] uppercase text-white/55">Vendor</div>
      <h1 className="font-display text-4xl sm:text-5xl uppercase tracking-tight mt-2">{org.brand_name}</h1>
      <div className="mt-2 text-sm text-white/55 flex items-center gap-3 flex-wrap">
        <span>{org.legal_name}</span>
        {org.verified && (
          <span className="px-2 py-0.5 rounded-full text-[10px] tracking-[0.18em] uppercase bg-emerald-500/15 text-emerald-300">
            <BadgeCheck size={10} className="inline mr-1" /> Verified
          </span>
        )}
      </div>

      {/* Track-specific dashboard (Technical vs Supplies) */}
      {org.vendor_classification === "technical" && (
        <div className="mt-8">
          <VendorTechnicalPanel org={org} />
        </div>
      )}
      {org.vendor_classification === "supplies" && (
        <div className="mt-8">
          <VendorSuppliesPanel org={org} />
        </div>
      )}

      <div className="mt-8 grid lg:grid-cols-3 gap-6">
        {/* Sidebar: stats */}
        <div className="space-y-6">
          <div className="cs-card rounded-2xl p-5" data-testid="vendor-stats-card">
            <div className="text-[10px] tracking-[0.28em] uppercase text-white/55">Push Slots</div>
            <div className="font-display text-3xl mt-1">1 / 1</div>
            <div className="text-[11px] text-white/45 mt-0.5">monthly · resets 1st</div>
            <Button className="mt-4 h-9 px-4 rounded-full text-[10px] uppercase tracking-[0.22em] bg-white/10 hover:bg-white/15 text-white w-full"
              data-testid="schedule-push-btn">
              <Send size={11} className="mr-1.5" /> Schedule Push
            </Button>
          </div>
          <div className="cs-card rounded-2xl p-5" data-testid="vendor-leads-card">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[10px] tracking-[0.28em] uppercase text-white/55">Lead Inbox</div>
                <div className="font-display text-3xl mt-1">0</div>
              </div>
              <Inbox size={18} className="text-white/40" />
            </div>
            <div className="text-[11px] text-white/45 mt-2">
              Productions reaching out land here. Empty for now — ships with the procurement gate.
            </div>
          </div>
          <div className="cs-card rounded-2xl p-5" data-testid="vendor-cats-card">
            <div className="text-[10px] tracking-[0.28em] uppercase text-white/55">Listed Categories</div>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {pickedCats.map((c) => (
                <span key={c.key} className="text-[10px] tracking-[0.16em] uppercase px-2 py-0.5 rounded-full bg-white/8 text-white/75"
                  data-testid={`vendor-cat-pill-${c.key}`}>
                  {c.label}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Catalog (2 col) */}
        <div className="lg:col-span-2 cs-card rounded-2xl p-6" data-testid="vendor-catalog-card">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[10px] tracking-[0.28em] uppercase text-white/55">Catalog</div>
              <h2 className="font-display text-xl uppercase tracking-tight">
                {catalog.length} item{catalog.length === 1 ? "" : "s"} listed
              </h2>
            </div>
            <Dialog open={addOpen} onOpenChange={setAddOpen}>
              <DialogTrigger asChild>
                <Button className="h-9 px-4 rounded-full text-[10px] uppercase tracking-[0.22em] btn-themed"
                  data-testid="add-catalog-btn">
                  <Plus size={12} className="mr-1.5" /> Add Item
                </Button>
              </DialogTrigger>
              <AddCatalogDialog onOpenChange={setAddOpen} orgId={org.id} categories={pickedCats} onAdded={refresh} />
            </Dialog>
          </div>

          {catalog.length === 0 ? (
            <div className="text-sm text-white/55 py-10 text-center" data-testid="catalog-empty">
              No items yet. Click <strong>Add Item</strong> to list your first product/service.
              Productions will see these when sourcing equipment.
            </div>
          ) : (
            <div className="space-y-2" data-testid="catalog-list">
              {catalog.map((it) => (
                <div key={it.id} className="p-3 rounded-lg bg-white/5 border border-white/10 flex items-center gap-3" data-testid={`catalog-row-${it.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-white">{it.name}</span>
                      <span className="text-[10px] tracking-[0.18em] uppercase text-white/55">{it.category_label}</span>
                    </div>
                    {it.description && (
                      <div className="text-[11px] text-white/55 mt-1 truncate">{it.description}</div>
                    )}
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-display text-white">
                      {it.price_inr > 0 ? `₹${Number(it.price_inr).toLocaleString("en-IN")}` : "—"}
                    </div>
                    <div className="text-[10px] tracking-[0.18em] uppercase text-white/40">/ {it.unit}</div>
                  </div>
                  <button onClick={() => removeItem(it.id)}
                    className="text-white/40 hover:text-red-300 p-1"
                    data-testid={`catalog-remove-${it.id}`}
                    aria-label="Remove">
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="mt-10">
        <OrgTeamPanel org={org} />
      </div>
    </div>
  );
}

function AddCatalogDialog({ onOpenChange, orgId, categories, onAdded }) {
  const [name, setName] = useState("");
  const [categoryKey, setCategoryKey] = useState(categories[0]?.key || "");
  const [description, setDescription] = useState("");
  const [price, setPrice] = useState("");
  const [unit, setUnit] = useState("day");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/orgs/${orgId}/catalog`, {
        name, category_key: categoryKey, description,
        price_inr: price ? Number(price) : 0, unit,
      });
      toast.success("Added to catalog");
      onOpenChange(false);
      await onAdded?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not add");
    } finally {
      setBusy(false);
    }
  };

  return (
    <DialogContent className="bg-[#0c0c0e] border-white/10 max-w-lg" data-testid="add-catalog-dialog">
      <DialogHeader>
        <DialogTitle className="font-display uppercase tracking-tight">Add Catalog Item</DialogTitle>
        <DialogDescription className="text-white/55 text-xs">
          Productions will see this when sourcing for their projects.
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-4">
        <div>
          <Label className="text-[10px] uppercase tracking-[0.22em] text-white/55">Name *</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)}
            placeholder='e.g. ARRI Alexa Mini LF Body'
            className="mt-2 h-10 bg-black/40 border-white/15" data-testid="catalog-name-input" />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.22em] text-white/55">Category *</Label>
          <Select value={categoryKey} onValueChange={setCategoryKey}>
            <SelectTrigger className="mt-2 h-10 bg-black/40 border-white/15" data-testid="catalog-cat-select">
              <SelectValue placeholder="Pick a category" />
            </SelectTrigger>
            <SelectContent>
              {categories.map((c) => (
                <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.22em] text-white/55">Description</Label>
          <Textarea value={description} onChange={(e) => setDescription(e.target.value)}
            placeholder="Specs, condition, included accessories…"
            className="mt-2 bg-black/40 border-white/15" data-testid="catalog-desc-input" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-[10px] uppercase tracking-[0.22em] text-white/55">Price (₹)</Label>
            <Input type="number" value={price} onChange={(e) => setPrice(e.target.value)}
              placeholder="0"
              className="mt-2 h-10 bg-black/40 border-white/15" data-testid="catalog-price-input" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.22em] text-white/55">Per</Label>
            <Select value={unit} onValueChange={setUnit}>
              <SelectTrigger className="mt-2 h-10 bg-black/40 border-white/15" data-testid="catalog-unit-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="day">Day</SelectItem>
                <SelectItem value="hour">Hour</SelectItem>
                <SelectItem value="piece">Piece</SelectItem>
                <SelectItem value="week">Week</SelectItem>
                <SelectItem value="project">Project</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <Button onClick={submit} disabled={!name || !categoryKey || busy}
          className="w-full btn-themed h-11 rounded-full text-xs uppercase tracking-[0.25em]"
          data-testid="catalog-submit">
          {busy ? "Adding…" : "Add to Catalog"}
        </Button>
      </div>
    </DialogContent>
  );
}
